import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isUserLoggedIn } from "../../utils/ecommerce/auth-session";
import { getCartItemCount } from "../../utils/ecommerce/cart-storage";
import { EC } from "../../utils/const/ecommerce-colors-const";
import LanguageDropdown from "../LanguageDropdown";
import { APP_LANGUAGES } from "../../utils/i18n/app-languages";
import { useAppLanguage } from "../../utils/i18n/use-app-language";

export default function EcommerceHeader({ onNavClick, activeSection }) {
    const history = useHistory();
    const { t } = useTranslation();
    const {
        selectedLanguage,
        setSelectedLanguage,
        isLanguageDropdownOpen,
        setIsLanguageDropdownOpen,
    } = useAppLanguage();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [cartCount, setCartCount] = useState(0);
    const [loggedIn, setLoggedIn] = useState(false);

    useEffect(() => {
        const syncCart = () => {
            setLoggedIn(isUserLoggedIn());
            setCartCount(isUserLoggedIn() ? getCartItemCount() : 0);
        };
        syncCart();
        window.addEventListener("comprartodo-cart-updated", syncCart);
        window.addEventListener("focus", syncCart);
        return () => {
            window.removeEventListener("comprartodo-cart-updated", syncCart);
            window.removeEventListener("focus", syncCart);
        };
    }, []);

    const languages = APP_LANGUAGES;

    const navItems = [
        { id: "inicio", labelKey: "label_inicio" },
        { id: "categorias", labelKey: "ct_nav_categories" },
        { id: "productos", labelKey: "ct_nav_products" },
        { id: "contacto", labelKey: "label_contacto" },
    ];

    const handleNav = (id) => {
        setMobileNavOpen(false);
        onNavClick?.(id);
    };

    return (
        <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center py-3.5 gap-3">
                    <button
                        type="button"
                        onClick={() => handleNav("inicio")}
                        className={`flex items-center gap-2 text-left font-extrabold text-xl sm:text-2xl tracking-tight transition shrink-0 ${EC.brand.textLg}`}
                    >
                        <span className="text-2xl shrink-0" aria-hidden>🛒</span>
                        <span className="truncate max-w-[9rem] xs:max-w-none">{t("ct_brand")}</span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-6">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => handleNav(item.id)}
                                className={`font-medium transition ${activeSection === item.id ? EC.nav.active : EC.nav.link
                                    }`}
                            >
                                {t(item.labelKey)}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                        <button
                            type="button"
                            className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 border border-slate-200"
                            onClick={() => setMobileNavOpen((o) => !o)}
                            aria-expanded={mobileNavOpen}
                            aria-label="Menu"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {mobileNavOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                        <div className="hidden sm:block">
                            <LanguageDropdown
                                languages={languages}
                                selectedLanguage={selectedLanguage}
                                setSelectedLanguage={setSelectedLanguage}
                                isOpen={isLanguageDropdownOpen}
                                setIsOpen={setIsLanguageDropdownOpen}
                            />
                        </div>
                        {loggedIn && cartCount > 0 && (
                            <span
                                className={`flex items-center gap-1.5 ${EC.cart.badge}`}
                                aria-label={t("ct_cart_items", { count: cartCount })}
                            >
                                🛒 {cartCount}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => history.push(loggedIn ? "/dashboard" : "/login")}
                            className={EC.btn.primaryMd}
                        >
                            {loggedIn ? t("ct_my_account") : t("label_iniciar_sesion")}
                        </button>
                    </div>
                </div>

                {mobileNavOpen && (
                    <nav className="lg:hidden border-t border-slate-100 py-4 flex flex-col gap-3">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => handleNav(item.id)}
                                className={`text-left font-medium px-1 ${EC.nav.link}`}
                            >
                                {t(item.labelKey)}
                            </button>
                        ))}
                    </nav>
                )}

                <div className="sm:hidden border-t border-slate-100 py-2 flex justify-center">
                    <LanguageDropdown
                        languages={languages}
                        selectedLanguage={selectedLanguage}
                        setSelectedLanguage={setSelectedLanguage}
                        isOpen={isLanguageDropdownOpen}
                        setIsOpen={setIsLanguageDropdownOpen}
                    />
                </div>
            </div>
        </header>
    );
}

export function EcommerceFooter({ t, whatsappUrl, currentYear }) {
    return (
        <footer id="contacto" className="bg-slate-950 text-slate-300 py-12 px-4 sm:px-6 lg:px-8 pb-28 sm:pb-12">
            <div className="max-w-7xl mx-auto grid gap-10 md:grid-cols-3">
                <div>
                    <p className="text-white font-bold text-lg flex items-center gap-2">
                        <span aria-hidden>🛒</span> {t("ct_brand")}
                    </p>
                    <p className="text-sm text-slate-400 mt-3 leading-relaxed">{t("ct_footer_line")}</p>
                </div>
                <div>
                    <p className="text-white font-semibold mb-3">{t("ct_footer_categories_title")}</p>
                    <p className="text-sm text-slate-400 leading-relaxed">{t("ct_footer_categories_desc")}</p>
                </div>
                <div className="flex flex-col items-start gap-4">
                    {whatsappUrl && (
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl transition ${EC.footer.whatsapp}`}
                        >
                            WhatsApp
                        </a>
                    )}
                    <p className="text-sm text-slate-500">{t("ct_footer_share_hint")}</p>
                </div>
            </div>
            <div className="max-w-7xl mx-auto border-t border-slate-800 mt-10 pt-8 text-center text-sm text-slate-500">
                © {currentYear} {t("ct_brand")}. {t("te_footer_rights")}
            </div>
        </footer>
    );
}

export function WhatsAppFab({ whatsappUrl, ariaLabel }) {
    if (!whatsappUrl) return null;
    return (
        <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-[max(1.25rem,env(safe-area-inset-right))] z-50 flex h-14 w-14 items-center justify-center rounded-full ${EC.fab.whatsapp}`}
            aria-label={ariaLabel}
        >
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.883 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
        </a>
    );
}
